import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getFbCookies, trackPixelEvent } from "@/lib/tracking";
import { TRIPS } from "@/data/trips";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Loader2, Send, ChevronDown, Check, X } from "lucide-react";

export default function ContactForm({ trip, compact = false, idPrefix = "", showHeader = true }) {
  const navigate = useNavigate();
  const pid = (s) => (idPrefix ? `${idPrefix}-${s}` : s);
  const [selectedSlugs, setSelectedSlugs] = useState(trip ? [trip.slug] : []);
  const [tripsOpen, setTripsOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    adults: 2,
    children: 2,
    children_ages: "",
    message: "",
    accept_privacy: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const selectedTrips = selectedSlugs
    .map((slug) => TRIPS.find((t) => t.slug === slug))
    .filter(Boolean);

  const toggleTrip = (slug) => {
    setSelectedSlugs((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]: type === "checkbox" ? checked : type === "number" ? Number(value) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedTrips.length === 0) {
      setError("Selecciona al menos un viaje sobre el que quieres información.");
      return;
    }
    if (!form.accept_privacy) {
      setError("Debes aceptar la Política de Privacidad para poder enviar el formulario.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { accept_privacy, ...payload } = form;
      const tripSlugs = selectedTrips.map((t) => t.slug);
      const tripNames = selectedTrips.map((t) => t.name);
      const joinedNames = tripNames.join(", ");
      // Dispara Lead en Pixel (navegador) y captura event_id para deduplicar con CAPI
      const eventId = trackPixelEvent("Lead", {
        content_name: joinedNames,
        content_category: "viaje-esqui-familia",
        content_ids: tripSlugs,
        currency: "EUR",
      });
      const { fbc, fbp } = getFbCookies();
      await api.post("/leads", {
        trip_slugs: tripSlugs,
        trip_names: tripNames,
        ...payload,
        // Metadatos para Meta Conversions API (server-side)
        fbc,
        fbp,
        event_id: eventId,
        event_source_url: typeof window !== "undefined" ? window.location.href : undefined,
      });
      const params = new URLSearchParams({
        name: form.name,
        trip: joinedNames,
      });
      navigate(`/gracias-formulario?${params.toString()}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "No hemos podido enviar tu mensaje.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid={pid("contact-form")}
      className={`${
        compact ? "" : "card-soft p-6 md:p-10"
      } w-full grid grid-cols-1 md:grid-cols-2 gap-5`}
    >
      {showHeader && (
        <div className="md:col-span-2 mb-2">
          <p className="overline">Cuéntanos qué necesitáis</p>
          <h3 className="h2-display mt-2 text-3xl">Diseñamos vuestro viaje a medida, sin prisas.</h3>
          <p className="body-lg mt-3 max-w-2xl">
            Elige el viaje que más os encaje y Club Amistad contactará contigo para escuchar a tu familia
            y resolver todas tus dudas. Sin compromiso ni letra pequeña.
          </p>
        </div>
      )}

      <div className="md:col-span-2">
        <label className="field-label">¿Sobre qué viaje quieres información?</label>
        <Popover open={tripsOpen} onOpenChange={setTripsOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid={pid("form-trips-trigger")}
              className="field-input flex items-center justify-between gap-2 text-left"
            >
              <span className={selectedTrips.length ? "text-brand-ink" : "text-brand-muted"}>
                {selectedTrips.length === 0
                  ? "Elige el viaje que más te encaje"
                  : selectedTrips.length === 1
                  ? selectedTrips[0].name
                  : `${selectedTrips.length} viajes seleccionados`}
              </span>
              <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-[--radix-popover-trigger-width] max-h-72 overflow-y-auto p-1"
            data-testid={pid("form-trips-list")}
          >
            {TRIPS.map((t) => {
              const active = selectedSlugs.includes(t.slug);
              return (
                <button
                  key={t.slug}
                  type="button"
                  onClick={() => toggleTrip(t.slug)}
                  data-testid={pid(`form-trip-option-${t.slug}`)}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-brand-sand/60 transition-colors"
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                      active ? "bg-brand-accent border-brand-accent text-white" : "border-brand-ink/30"
                    }`}
                  >
                    {active && <Check className="h-3 w-3" />}
                  </span>
                  <span className="flex-1">
                    <span className="block text-brand-ink">{t.name}</span>
                    <span className="block text-xs text-brand-muted">
                      {t.dates_short}
                      {t.sold_out ? " · Completo" : ""}
                    </span>
                  </span>
                </button>
              );
            })}
          </PopoverContent>
        </Popover>

        {selectedTrips.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid={pid("form-trips-selected")}>
            {selectedTrips.map((t) => (
              <span
                key={t.slug}
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-sand px-3 py-1 text-xs text-brand-ink"
              >
                {t.name}
                <button
                  type="button"
                  onClick={() => toggleTrip(t.slug)}
                  data-testid={pid(`form-trip-remove-${t.slug}`)}
                  aria-label={`Quitar ${t.name}`}
                  className="text-brand-muted hover:text-brand-accent"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className="field-label" htmlFor={pid("name")}>Nombre y apellidos</label>
        <input
          id={pid("name")}
          name="name"
          value={form.name}
          onChange={handleChange}
          required
          minLength={2}
          data-testid={pid("form-name")}
          className="field-input"
          placeholder="¿Cómo te llamas?"
        />
      </div>
      <div>
        <label className="field-label" htmlFor={pid("email")}>Correo electrónico</label>
        <input
          id={pid("email")}
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
          required
          data-testid={pid("form-email")}
          className="field-input"
          placeholder="tu@email.com"
        />
      </div>
      <div>
        <label className="field-label" htmlFor={pid("phone")}>Teléfono</label>
        <input
          id={pid("phone")}
          name="phone"
          value={form.phone}
          onChange={handleChange}
          required
          data-testid={pid("form-phone")}
          className="field-input"
          placeholder="6XX XXX XXX"
        />
      </div>
      <div>
        <label className="field-label" htmlFor={pid("adults")}>Nº de adultos</label>
        <input
          id={pid("adults")}
          name="adults"
          type="number"
          min="1"
          max="20"
          value={form.adults}
          onChange={handleChange}
          required
          data-testid={pid("form-adults")}
          className="field-input"
        />
      </div>
      <div>
        <label className="field-label" htmlFor={pid("children")}>Nº de niños</label>
        <input
          id={pid("children")}
          name="children"
          type="number"
          min="0"
          max="20"
          value={form.children}
          onChange={handleChange}
          required
          data-testid={pid("form-children")}
          className="field-input"
        />
      </div>
      <div className="md:col-span-2">
        <label className="field-label" htmlFor={pid("children_ages")}>Edades de los niños</label>
        <input
          id={pid("children_ages")}
          name="children_ages"
          value={form.children_ages}
          onChange={handleChange}
          data-testid={pid("form-children-ages")}
          className="field-input"
          placeholder="Ej: 5 y 8 años"
        />
      </div>
      <div className="md:col-span-2">
        <label className="field-label" htmlFor={pid("message")}>Cuéntanos algo más (opcional)</label>
        <textarea
          id={pid("message")}
          name="message"
          rows={4}
          value={form.message}
          onChange={handleChange}
          data-testid={pid("form-message")}
          className="field-input resize-none"
          placeholder="¿Es la primera vez en la nieve? ¿Algo importante que debamos saber?"
        />
      </div>

      {error && (
        <p data-testid={pid("form-error")} className="md:col-span-2 text-sm text-red-600">
          {error}
        </p>
      )}

      <div className="md:col-span-2 pt-2">
        <label className="flex items-start gap-3 text-sm text-brand-ink/80 cursor-pointer" htmlFor={pid("accept_privacy")}>
          <input
            id={pid("accept_privacy")}
            name="accept_privacy"
            type="checkbox"
            checked={form.accept_privacy}
            onChange={handleChange}
            required
            data-testid={pid("form-accept-privacy")}
            className="mt-1 h-4 w-4 accent-brand-accent shrink-0"
          />
          <span>
            He leído y acepto la{" "}
            <a
              href="/politica-de-privacidad"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-brand-primary hover:text-brand-accent"
            >
              Política de Privacidad
            </a>{" "}
            y el{" "}
            <a
              href="/aviso-legal"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-brand-primary hover:text-brand-accent"
            >
              Aviso Legal
            </a>{" "}
            de Club Amistad. Consiento el tratamiento de mis datos con la finalidad de gestionar mi consulta y recibir información sobre los viajes solicitados.
          </span>
        </label>
      </div>

      <div className="md:col-span-2 flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-2">
        <p className="text-xs text-brand-muted max-w-sm">
          Al enviar este formulario aceptas que Club Amistad contacte contigo para resolver tus dudas
          sobre el viaje elegido.
        </p>
        <button
          type="submit"
          disabled={loading}
          data-testid={pid("form-submit")}
          className="btn-primary md:w-auto w-full disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {loading ? "Enviando…" : "Quiero que me contactéis"}
        </button>
      </div>
    </form>
  );
}
